import Box from '@material-ui/core/Box'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import IconCancel from '@material-ui/icons/Cancel'
import { useState } from 'react'
import {
  Button,
  FormWithRedirect,
  required,
  SaveButton,
  SelectInput,
  TextInput,
  useNotify,
  useRefresh,
} from 'react-admin'

import { captureExceptionIfProduction } from 'helpers/utils'
import { COUNTRIES, getCountryFromCode } from '../../helpers/countries'
import { getStateChoices } from '../../helpers/states'
import { validateNoSpecialChars } from '../../helpers/validators'
import AddressAutoComplete from './AddressAutoComplete'

type AddressInputFormProps = {
  showCountry?: boolean
  isBilling?: boolean
  sourceParent?: string
  showAutoComplete?: boolean
  setFieldValue?: (field: string, value: any) => void
  isAllUSStates?: boolean
}

export const AddressInputForm = ({
  showCountry,
  isBilling,
  sourceParent,
  showAutoComplete,
  setFieldValue,
  isAllUSStates,
}: AddressInputFormProps) => {
  const [country, setCountry] = useState('US')
  const selectedCountryData = getCountryFromCode(country)
  const stateChoices = getStateChoices(selectedCountryData?.states(isAllUSStates))
  const addressPrefix = isBilling ? 'Billing Address' : 'Shipping Address'
  const sourcePrefix = sourceParent ? sourceParent + '.' : ''

  const handleCountryChange = (event: any) => {
    setCountry(event.target.value)
  }

  return (
    <>
      {showCountry && (
        <Box>
          <SelectInput
            source={sourcePrefix + 'country'}
            label={addressPrefix + ' Country'}
            initialValue={country}
            choices={COUNTRIES}
            validate={required()}
            style={{ width: '50%' }}
            onChange={handleCountryChange}
          />
        </Box>
      )}
      {showAutoComplete && (
        <Box marginBottom={2}>
          <AddressAutoComplete
            label="Address Autocomplete"
            setLine1={(value: any) => setFieldValue && setFieldValue(sourcePrefix + 'line1', value)}
            setCity={(value: any) => setFieldValue && setFieldValue(sourcePrefix + 'city', value)}
            setState={(value: any) => setFieldValue && setFieldValue(sourcePrefix + 'state', value)}
            setZip={(value: any) => setFieldValue && setFieldValue(sourcePrefix + 'zip', value)}
          />
        </Box>
      )}
      <TextInput
        source={sourcePrefix + 'line1'}
        label={addressPrefix + ' Line 1'}
        validate={[required(), validateNoSpecialChars]}
        fullWidth
      />
      <TextInput
        source={sourcePrefix + 'line2'}
        label={addressPrefix + ' Line 2'}
        validate={[validateNoSpecialChars]}
        fullWidth
      />
      <TextInput
        source={sourcePrefix + 'city'}
        label={addressPrefix + ' City'}
        validate={[required(), validateNoSpecialChars]}
        fullWidth
      />
      <SelectInput
        source={sourcePrefix + 'state'}
        label={addressPrefix + ' State'}
        choices={stateChoices}
        validate={required()}
        style={{ width: '100%' }}
      />
      <TextInput
        source={sourcePrefix + 'zip'}
        label={addressPrefix + ' Zip'}
        validate={[required(), validateNoSpecialChars]}
        fullWidth
        parse={(value: any) => (value ? value.toUpperCase() : value)}
        format={(value: any) => (value ? value.toUpperCase() : '')}
      />
    </>
  )
}

type AddressEditDialogProps = {
  submit: (id: string, values: any) => Promise<any>
  address_name: string
  record: any
  isBilling?: boolean
}

export const AddressEditDialog = ({ submit, address_name, record, isBilling }: AddressEditDialogProps) => {
  const addressData = record[address_name]
  const [loading, setLoading] = useState(false)
  const [showDialog, setShowDialog] = useState(false)
  const notify = useNotify()
  const refresh = useRefresh()
  const handleClick = () => setShowDialog(true)

  const handleClose = () => setShowDialog(false)

  const handleSubmit = async (values: any) => {
    setLoading(true)
    submit(record.id, values)
      .then(() => {
        notify(`Address updated!`, { type: 'success' })
        refresh()
        setShowDialog(false)
      })
      .catch((error) => {
        notify(`Oops! Something went wrong: ${error.message}`, { type: 'error' })
        captureExceptionIfProduction(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <>
      <Button
        style={{ marginBottom: '20px', marginTop: '20px' }}
        color="primary"
        variant="contained"
        onClick={handleClick}
        label={addressData ? 'Edit' : 'Add'}
      />
      <Dialog fullWidth open={showDialog} onClose={handleClose}>
        <DialogTitle>Edit Address</DialogTitle>
        <FormWithRedirect
          initialValues={addressData}
          resource={address_name}
          save={handleSubmit}
          mutators={{
            // setFieldValue is a function that accepts (field, value) args and is used to update a field with a specific value
            // this is useful when there are e.g. dependencies between fields (e.g. address autocomplete can populate city, state, zip fields)
            setFieldValue: ([field, value]: any, state: any, { changeValue }: any) => {
              changeValue(state, field, () => value)
            },
          }}
          render={({ handleSubmitWithRedirect, saving, form }) => (
            <>
              <DialogContent>
                <AddressInputForm
                  isBilling={isBilling}
                  setFieldValue={form.mutators.setFieldValue}
                  showAutoComplete
                />
              </DialogContent>
              <DialogActions>
                <Button label="ra.action.cancel" onClick={handleClose} disabled={loading}>
                  <IconCancel />
                </Button>
                <SaveButton
                  handleSubmitWithRedirect={handleSubmitWithRedirect}
                  saving={saving}
                  disabled={loading}
                />
              </DialogActions>
            </>
          )}
        />
      </Dialog>
    </>
  )
}
